import { Checkbox, Col, DatePicker, Form, Icon, Input, Row } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import useFetch from 'elements/Hooks/useFetch'
import { get, isEmpty } from 'lodash'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

import { compose } from 'recompose'
import { EnquiriesSelect } from '../../Sales/Quotes/AddQuote/AddQuote'
import CustomSpin from '../../../elements/CustomSpin'
import DeliveryDetails from '../../../elements/DeliveryDetails'
import withKeyValue from '../../../elements/HOC/WithKeyValue'
import withSort from '../../../elements/HOC/withSort'
import IconText from '../../../elements/IconText'
import InfoTooltip from '../../../elements/InfoTooltip'
import RegisteredPartnerDetails from '../../../elements/RegisteredPartnerDetails'
import CustomSelect from '../../../elements/Select/CustomSelect'
import withData from '../../../elements/Select/withData'
import { fullWidthFormItemLayout, rules, threeFormItemLayout } from '../../../modules/FormItems'
import { dateVal, idMaker } from '../../../modules/Utils'

const RFQTypeSelect = compose(withData({ data: ['Sales', 'Internal'] }), withKeyValue(), withSort)(CustomSelect)

const { TextArea } = Input

const ids = idMaker()

const instructions = `Please provide your offer for the above items.\n\nWhen quoting please indicate:\n
1) Your prices export packed including Payment Terms
2) Validity of your offer (we would appreciate 90 days)
3) Indicate if cargo or components are Dangerous Goods - if so provide Material Safety Data Sheets
4) Indicate if the cargo is Export Restricted and require Export License, providing compliance requirements
5) Provide Technical Data Sheets if developed
6) Delivery Forecast from Receipt of Order (as per OEM quoted terms)
7) Country of Origin
8) HS Duty Tariff Code
9) Indicate the estimated Packed Shipping Weights and Dimensions and whether the items are stackable - all packaging to be ISPM15 Treated and Stamped
10) Provide details of Warranties or Limitations
`

function RFQForm(props) {
  const { rfq, onChangeEnquirer, setContactPersonLoading, enquiryID, type } = props
  const { getFieldDecorator, resetFields, getFieldValue, setFieldsValue } = props.form

  const eID = getFieldValue('enquiry._id') || get(rfq, 'enquiry._id')

  const enqKey = eID && eID.replace('activities/', '')

  const isConfirmed = (get(rfq, 'status') !== 'Draft' && get(rfq, 'status') !== 'Revised' && type !== 'new')
  //const isConfirmed = false

  useEffect(() => {
    if (getFieldValue('enquirer._id')) {
      onChangeEnquirer(getFieldValue('enquirer._id'))
      props.form.setFieldsValue({
        'enquirer_rep._id': get(enquiry, 'enquirer_rep._id'),
      })
    } else {
      setContactPersonLoading(false)
    }
  }, [])

  const { fetching, data: enquiry } = useFetch({
    url: `enquiries/${(getFieldValue('enquiry._id') || '').replace('activities/', '')}`,
    dontFetch: isEmpty(getFieldValue('enquiry._id')) || rfq._id,
  })

  React.useEffect(() => {
    if (!isEmpty(enquiry) && !rfq._id)
      setFieldsValue({
        title: enquiry.title,
        'company._id': get(enquiry, 'company._id'),
      })
  }, [enquiry._rev])

  const [showDesc, setShowDesc] = React.useState(() => !isEmpty(rfq.description))

  return (
    <Form>
      <CustomSpin spinning={fetching}>
        <Row>
          <Col span={8}>
            <FormItem {...threeFormItemLayout} label="RFQ Type">
              {getFieldDecorator('type', {
                initialValue: get(rfq, 'type') || 'Sales',
                rules: rules.select,
              })(<RFQTypeSelect disabled={isConfirmed} style={{ width: '80%' }} />)}
            </FormItem>
          </Col>
          {getFieldValue('type') === 'Sales' && (
            <Col span={8}>
              <Form.Item {...threeFormItemLayout} label="Enquiry">
                {getFieldDecorator('enquiry._id', {
                  initialValue: get(rfq, 'enquiry._id') || enquiryID,
                  rules: rules.select,
                })(
                  <EnquiriesSelect
                    style={{ width: '80%' }}
                    keyAtr={'_id'}
                    valueAtr={'display'}
                    placeholder={'Select an enquiry'}
                    disabled={isConfirmed}
                  />
                )}
                <Link target={'_blank'} to={`/clients/enquiries/${enqKey}/update`}>
                  <Icon style={{ marginLeft: 5 }} type="fullscreen" />
                </Link>
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <FormItem
              {...threeFormItemLayout}
              label={
                <InfoTooltip value={'Requirement'} title={'Note: Logistics should be selected for Freight RFQs'} />
              }>
              {getFieldDecorator('requirement', {
                initialValue: get(rfq, 'requirement') || 'Supply',
                rules: rules.select,
              })(<RFQRequirementSelect disabled={isConfirmed} style={{ width: '80%' }} />)}
            </FormItem>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item {...fullWidthFormItemLayout} label="Title">
              {getFieldDecorator('title', {
                initialValue: get(rfq, 'title'),
                rules: rules.select,
              })(<Input disabled={isConfirmed} placeholder="Title" />)}
              <IconText
                text={showDesc !== true ? 'Optionally Add RFQ Summary' : 'Remove RFQ Summary'}
                type={showDesc !== true ? 'plus-circle' : 'minus-circle'}
                onClick={() => setShowDesc(!showDesc)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          {showDesc === true && (
            <Col span={24}>
              <Form.Item {...fullWidthFormItemLayout} label={'RFQ Summary'}>
                {getFieldDecorator('description', {
                  initialValue: get(rfq, 'description'),
                  //rules: rules.input,
                })(<TextArea disabled={isConfirmed} placeholder={'Summary'} />)}
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row>
          <Col span={8}>
            <Form.Item {...threeFormItemLayout} label="RFQ Date">
              {getFieldDecorator('date.created', {
                initialValue: dateVal(rfq, 'date.created') || moment(),
                rules: rules.select,
              })(<DatePicker disabled={isConfirmed} style={{ width: '80%' }} />)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item {...threeFormItemLayout} label="Response Due">
              {getFieldDecorator('date.due', {
                initialValue: dateVal(rfq, 'date.due'),
                rules: rules.select,
              })(<DatePicker disabled={isConfirmed} style={{ width: '80%' }} />)}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              {...threeFormItemLayout}
              label={
                <InfoTooltip
                  value={'Auto Reminder'}
                  title={'If checked auto reminders will be sent 5, 2 & 1 day before Response Due Date'}
                />
              }>
              {getFieldDecorator('autoReminder', {
                initialValue: get(rfq, 'autoReminder') !== false,
                valuePropName: 'checked',
              })(<Checkbox placeholder="autoReminder" />)}
            </Form.Item>
          </Col>
        </Row>
        <Row style={{ paddingBottom: '10px', paddingTop: '10px' }}>
          <h3 style={{ color: 'rgb(43,187,143' }}>VENDOR ACCOUNT</h3>
        </Row>
        <Row>
          <RegisteredPartnerDetails
            viewOnly={isConfirmed}
            doc={rfq}
            {...{
              getFieldDecorator,
              getFieldValue,
              setFieldsValue,
              type: 'rfq',
              showRepEmail: true,
            }}
          />
        </Row>

        <Row>
          <DeliveryDetails
            viewOnly={isConfirmed}
            doc={rfq}
            {...{
              getFieldDecorator,
              getFieldValue,
              setFieldsValue,
              resetFields,
              type: 'RFQ',
            }}
          />
        </Row>
        <Row style={{ paddingBottom: '10px', paddingTop: '10px' }}>
          <h3 style={{ color: 'rgb(43,187,143' }}>RFQ INSTRUCTIONS</h3>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item {...fullWidthFormItemLayout} label="Instructions">
              {getFieldDecorator('instructions', {
                initialValue: get(rfq, 'instructions') || instructions,
                rules: rules.select,
              })(<TextArea disabled={isConfirmed} autosize={true} />)}
            </Form.Item>
          </Col>
        </Row>
      </CustomSpin>
    </Form>
  )
}

const RFQRequirementSelect = compose(
  withData({
    data: [
      { value: 'Logistics', key: 'Logistics' },
      { value: 'Service', key: 'Service' },
      { value: 'Supply', key: 'Supply' },
    ],
  }),
  withSort
)(CustomSelect)

RFQForm.propTypes = {}
RFQForm.defaultProps = {}

export default Form.create()(RFQForm)
